import React, { useEffect, useMemo, useRef, useState } from "react";
import RffFormChanges from "../components/finalForm/RffFormChanges";

import { isFunction } from "lodash";
import { Box, TextField, Tooltip } from "@mui/material";
import { amber, grey } from "@mui/material/colors";
import { WarningAmberOutlined } from "@mui/icons-material";
import { Field, useForm, useFormState } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import RffBooleanSwitchField from "../components/finalForm/inputs/RffBooleanSwitchField";
import RffSelectField from "../components/finalForm/inputs/RffSelectField";
import RffGroup from "../components/finalForm/shared/RffGroup";
import RffInputWrapper from "../components/finalForm/shared/RffInputWrapper";
import SelectFileAssignmentInput from "../components/inputs/SelectFileAssignmentInput";
import TextInput from "../components/inputs/TextInput";
import Flx from "../components/layout/Flx";
import Txt from "../components/typography/Txt";
import { selectOptionsDocumentStatusWithLabels } from "../constants/selectOptions/selectOptionsDocumentStatus";
import { useLoanFilesHook } from "../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../redux/actions/loanDrilldownActions";
import { VALIDATOR_REQUIRE } from "../utils/finalForm/validators/VALIDATOR_REQUIRE";
import {
  DUPLICATE_NAME_MESSAGE,
  findDuplicateDisplayName,
} from "../utils/documents/duplicateDisplayName";
import DocExpirationCell from "../screens/LoanDrilldownScreen/tabs/LoanDrilldownDocumentsTab/DocExpirationCell";
import { AutoDurationPill } from "../screens/LoanDrilldownScreen/tabs/LoanDrilldownDocumentsTab/DocDurationCell";
import { DOCUMENT_SHARE_FLAGS } from "../utils/documents/documentShareFlags";
import {
  computeExpirationDate,
  deriveDualDateFields,
  deriveFromExpirationDate,
  getExpirationDaysForDocType,
  isDualDateDocType,
  isExplicitExpirationDocType,
  isHardDurationDocType,
} from "../constants/documents/docTypeExpirationDurations";

const UpdateLoanDocumentMetadataForm = ({ file, loanId, onSuccessFn }) => {
  const { loading, updateLoanDocMetadata } = useLoanFilesHook();
  const dispatch = useDispatch();
  // The loan's other documents, for the duplicate display-name check. Both
  // callers (the Documents tab's Edit Details dialog and the file preview
  // modal) edit a doc on the loan currently in `loanDrilldown`; if the store
  // is holding some other loan the check simply doesn't apply.
  const loanDocuments = useSelector(
    (state) => state.loanDrilldown?.loanDocuments,
  );
  const fileId = file?._id || file?.oid;
  const validateDisplayName = useMemo(() => {
    const docs = loanDocuments || [];
    const knownDoc = docs.some((d) => d?._id === fileId);
    if (!knownDoc) return undefined;
    return (value) =>
      findDuplicateDisplayName(docs, fileId, value)
        ? DUPLICATE_NAME_MESSAGE
        : undefined;
  }, [loanDocuments, fileId]);
  const onSubmit = (changedValues, { form, values, changes }) => {
    const payload = [{ _id: file._id || file?.oid, ...changedValues }];
    // console.log("payload", payload);

    updateLoanDocMetadata({
      loanId: loanId,
      data: payload,
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        if (isFunction(onSuccessFn)) {
          onSuccessFn(data);
        }
      },
    });
  };

  const initialValues = useMemo(() => {
    // `excludeFromExport` and the three share flags come back from the
    // backend as real booleans, `null` (never set), or "True"/"False"
    // strings — coerce all of them to booleans so the switches render
    // correctly and dirty-state tracking works.
    const toBool = (v) =>
      typeof v === "boolean"
        ? v
        : typeof v === "string"
          ? v === "true" || v === "True"
          : false;
    return {
      file_display_name: file?.file_display_name,
      docGroup: file?.docGroup,
      docType: file?.docType,
      documentStatus: file?.documentStatus,
      isHidden: toBool(file?.isHidden),
      reportEffectiveDate: file?.reportEffectiveDate || null,
      reportGuidelineExpirationDays:
        file?.reportGuidelineExpirationDays ?? null,
      excludeFromExport: toBool(file?.excludeFromExport),
      ...DOCUMENT_SHARE_FLAGS.reduce((acc, f) => {
        acc[f.field] = toBool(file?.[f.field]);
        return acc;
      }, {}),
    };
  }, [file]);

  return (
    <RffFormChanges
      loading={loading}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <RffDocumentMetaData
        fileNameAsLoaded={file?.file_name_as_loaded}
        validateDisplayName={validateDisplayName}
      />
    </RffFormChanges>
  );
};

const RffDocumentMetaData = ({ sx, fileNameAsLoaded, validateDisplayName }) => {
  const {
    values: { docGroup, docType },
  } = useFormState();
  const { batch, change } = useForm();

  const onDocGroupChange = (dg, dt) => {
    // Mirror the upload queue's handleRowDocTypeChange: when the doc type
    // changes, (re)stamp the auto validity window for the new type — dual-date
    // types (statements) derive their window from picked dates so they start
    // empty; every other type takes its mapped default (or null when unmapped).
    const stampedDays = isDualDateDocType(dt)
      ? null
      : getExpirationDaysForDocType(dt);
    batch(() => {
      change("docGroup", dg);
      change("docType", dt); // Reset docType when docGroup changes
      change("reportGuidelineExpirationDays", stampedDays ?? null);
    });
  };

  return (
    <RffGroup sx={sx} rowSpacing={3.2} columnSpacing={3.2}>
      <RffFileDisplayName
        name="file_display_name"
        label="File Display Name"
        fileNameAsLoaded={fileNameAsLoaded}
        validate={validateDisplayName}
      />
      <RffInputWrapper label={`Document Type`} size={6}>
        <SelectFileAssignmentInput
          placeholder="Search document categories and types..."
          onChange={(params) => {
            onDocGroupChange(params?.docGroup, params?.docType);
          }}
          onlyDisplayDocType
          docGroup={docGroup}
          docType={docType}
          // sx={{ mb: 3 }}
        />
      </RffInputWrapper>
      <RffSelectField
        name="documentStatus"
        label="Document Status"
        size={6}
        options={selectOptionsDocumentStatusWithLabels}
      />
      <RffValiditySection />
      <RffBooleanSwitchField
        name="isHidden"
        label="Hidden"
        description="Hidden files are excluded from default file lists and exports"
      />
      <RffBooleanSwitchField
        name="excludeFromExport"
        label="Exclude from export"
        description="Skip this file when generating closing or third-party export packages."
      />
      {/* The three share flags that drive the flag-filtered document
          packages — see utils/documents/documentShareFlags.js. Rendered
          from the registry so this form, the manager table's columns, and
          the bulk-edit popover can never drift apart. */}
      {DOCUMENT_SHARE_FLAGS.map((f) => (
        <RffBooleanSwitchField
          key={f.field}
          name={f.field}
          label={f.label}
          description={f.description}
        />
      ))}
    </RffGroup>
  );
};

// Doc-type-aware validity editor. Mirrors the upload queue + document manager
// table (see LoanDrilldownDocumentsTab's UploadQueue*Cell + docTypeExpiration
// Durations.js §4): the stored model is always `reportEffectiveDate` (start) +
// `reportGuidelineExpirationDays` (window), but how those are set depends on the
// doc type —
//   • Explicit expiration (license/passport): Effective + Duration are N/A; the
//     user picks the EXPIRATION date, which back-derives effective=today +
//     days=(expiration − today).
//   • Dual date (bank/brokerage statements): the user picks BOTH a start date
//     and an expiration date; Duration is N/A and stored as (expiration − start).
//   • Hard/fixed types: Effective is user-entered, Duration is the read-only
//     auto ⚡ pill, Expiration is computed.
//   • Soft/unmapped types: Effective + editable Duration, Expiration computed.
const NA_TEXT = <Txt sx={{ fontSize: 13, color: grey[400] }}>N/A</Txt>;

const RffValiditySection = () => {
  const {
    values: { docType, reportEffectiveDate, reportGuidelineExpirationDays },
  } = useFormState();
  const { batch, change } = useForm();

  const explicit = isExplicitExpirationDocType(docType);
  const dualDate = isDualDateDocType(docType);
  const hard = isHardDurationDocType(docType);

  const effectiveYmd = (reportEffectiveDate || "").slice(0, 10);
  const expirationYmd =
    computeExpirationDate(reportEffectiveDate, reportGuidelineExpirationDays) ||
    "";
  // A file with a validity window expires `days` after its effective start
  // date, so it needs a start date — but we never default it to today (these
  // reports are almost never effective on the upload date). Highlight instead.
  const windowDays =
    reportGuidelineExpirationDays ?? getExpirationDaysForDocType(docType);
  const needsDate = !explicit && !!windowDays && !reportEffectiveDate;

  const setEffective = (ymd) => {
    // Dual-date types: changing the start date preserves the currently picked
    // expiration and re-derives the duration from it.
    if (dualDate) {
      const currentExp = computeExpirationDate(
        reportEffectiveDate,
        reportGuidelineExpirationDays,
      );
      const derived = deriveDualDateFields(ymd, currentExp);
      batch(() => {
        change("reportEffectiveDate", derived.reportEffectiveDate);
        change(
          "reportGuidelineExpirationDays",
          derived.reportGuidelineExpirationDays,
        );
      });
      return;
    }
    change("reportEffectiveDate", ymd || null);
  };

  // The user picks the expiration date directly. Explicit-expiration types
  // (license/passport) back into effective=today + duration=(expiration −
  // today); dual-date types (statements) keep the picked start and set
  // duration=(expiration − start).
  const setExpiration = (ymd) => {
    const derived = dualDate
      ? deriveDualDateFields(reportEffectiveDate, ymd)
      : deriveFromExpirationDate(ymd);
    batch(() => {
      change("reportEffectiveDate", derived.reportEffectiveDate);
      change(
        "reportGuidelineExpirationDays",
        derived.reportGuidelineExpirationDays,
      );
    });
  };

  const setDuration = (raw) => {
    const parsed = raw === "" || raw == null ? null : Number(raw);
    const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    change("reportGuidelineExpirationDays", next);
  };

  return (
    <>
      {/* Effective Start Date */}
      <RffInputWrapper label="Effective Start Date" size={4.5}>
        {explicit ? (
          NA_TEXT
        ) : (
          <TextField
            type="date"
            size="small"
            fullWidth
            value={effectiveYmd}
            onChange={(e) => setEffective(e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: needsDate ? (
                <Tooltip
                  title={`${docType} expires ${windowDays} days after its effective date — enter the date the report states`}
                >
                  <WarningAmberOutlined
                    sx={{ fontSize: 16, color: amber[700], mr: 0.5 }}
                  />
                </Tooltip>
              ) : null,
            }}
            sx={{
              ...(needsDate && {
                bgcolor: amber[50],
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: amber[400],
                },
              }),
            }}
          />
        )}
      </RffInputWrapper>

      {/* Valid For (days) / Duration */}
      <RffInputWrapper label="Valid For (days)" size={3}>
        {explicit || dualDate ? (
          NA_TEXT
        ) : hard ? (
          // Hard types carry a fixed, read-only window sourced from the map.
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: 40,
              flexGrow: 1,
            }}
          >
            <AutoDurationPill
              sx={{ lineHeight: 2.5, flexGrow: 1 }}
              days={getExpirationDaysForDocType(docType)}
            />
          </Box>
        ) : (
          <ValidityDurationInput
            value={reportGuidelineExpirationDays}
            onChange={setDuration}
          />
        )}
      </RffInputWrapper>

      {/* Expiration */}
      <RffInputWrapper label="Expiration" size={4.5}>
        {explicit || dualDate ? (
          <TextField
            type="date"
            size="small"
            fullWidth
            value={expirationYmd}
            onChange={(e) => setExpiration(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        ) : (
          // Read-only computed expiration display (date + progress + countdown),
          // the same cell the manager table renders.
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: 40,
              border: "1px solid #E2E8F0",
              borderRadius: 1,
              px: 1,
            }}
          >
            <DocExpirationCell
              // column={false}
              // gap={3}
              // progressSx={{ flexGrow: 1, justifyContent: "flex-end" }}
              doc={{
                docType,
                reportEffectiveDate,
                reportGuidelineExpirationDays,
              }}
            />
          </Box>
        )}
      </RffInputWrapper>
    </>
  );
};

// Editable duration input with its OWN draft state, committing to the form only
// on blur / Enter (not per keystroke) — mirrors the upload queue's DurationInput
// so multi-digit entry is smooth. The draft syncs from props only while
// unfocused, so an external reset (e.g. a doc-type change clearing the value)
// still flows in.
const ValidityDurationInput = ({ value, onChange }) => {
  const [draft, setDraft] = useState(value ?? "");
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) setDraft(value ?? "");
  }, [value]);
  const commit = () => {
    focusedRef.current = false;
    onChange(draft);
  };
  return (
    <Flx ac g={0.75} sx={{ flexGrow: 1 }}>
      <TextField
        type="number"
        size="small"
        placeholder="—"
        value={draft}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        inputProps={{ min: 0, step: 1 }}
        sx={{
          // width: 80,
          flexGrow: 1,
          // "& .MuiInputBase-input": { textAlign: "center" },
        }}
      />
      <Txt sx={{ fontSize: 13, color: grey[500] }}>days</Txt>
    </Flx>
  );
};

const RffFileDisplayName = React.memo(function RffFileDisplayName({
  name,
  label,
  required,
  placeholder = "",
  suppressGrid = false,
  size,
  helperText,
  disabled,
  fileNameAsLoaded,
  testing,
  validate: extraValidate,
  sx,
}) {
  // Only recreate validator when "required" or the caller's extra rule changes.
  // The extra rule is the duplicate display-name guard — a name another file on
  // the loan already uses is refused here rather than silently renamed, so the
  // dialog can't "save" a change the operator will read as a revert.
  const validate = useMemo(() => {
    if (!required) return extraValidate;
    if (!extraValidate) return VALIDATOR_REQUIRE;
    return (value, allValues, meta) =>
      VALIDATOR_REQUIRE(value, allValues, meta) ||
      extraValidate(value, allValues, meta);
  }, [required, extraValidate]);

  return (
    <RffInputWrapper
      label={label}
      // suppressGrid={suppressGrid}
      size={size}
      required={required}
      testing={testing}
    >
      <Field
        name={name}
        validate={validate}
        // Subscribe to meta.initial for original value
        subscription={{
          value: true,
          touched: true,
          error: true,
          initial: true,
        }}
        render={({ input, meta }) => {
          const handleBlur = (event) => {
            // First update form state
            input.onBlur(event);

            // Determine original extension from initial value
            // console.log("meta", meta.data);
            const originalName = fileNameAsLoaded || "";
            const originalExtension = originalName
              .split(".")
              .pop()
              .toLowerCase();

            // Strip any extension from current input
            const currentValue = input.value || "";
            const baseName = currentValue.replace(/\.[^/.]+$/, "");

            // Reapply original extension
            const updatedName = `${baseName}.${originalExtension}`;
            if (updatedName !== currentValue) {
              input.onChange(updatedName);
            }
          };

          return (
            <TextInput
              {...input}
              sx={sx}
              onBlur={handleBlur}
              disabled={disabled}
              placeholder={placeholder}
              error={meta.touched && !!meta.error}
              helperText={(meta.touched && meta.error) || helperText}
            />
          );
        }}
      />
    </RffInputWrapper>
  );
});
export default UpdateLoanDocumentMetadataForm;
